import { SelectProps } from '@amzn/awsui-components-core/select';
import { ItemMetadata } from '../common/api.types';

// ============================================================================
// Type Aliases
// ============================================================================

/**
 * Mode for ManageRpnModal - determines create vs edit behavior
 */
export type ManageRpnMode = 'create' | 'edit';

/**
 * Approval status options for RPN form fields
 */
export type ApprovalStatus = 'yes' | 'no' | null;

/**
 * PRFAQ approval status options (includes N/A option)
 * Note: Backend expects capitalized values - "Yes", "No", "N/A"
 */
export type PrfaqApprovalStatus = 'Yes' | 'No' | 'N/A' | null;

/**
 * CreateOrUpdateRpnMutationEntity is to integrate with backend create or update RPN mutations.
 * Also used as the data structure for ManageRpnModal props when editing.
 * Backend always follows snake_case
 * FE also uses snake_case for Interfaces fields
 */
export interface CreateOrUpdateRpnMutationEntity {
  product_line_id: number;
  product_line: string;

  rpn_id: number | null; // null for Create, number for edit
  rpn_alias: string; // Program Name

  program_status: string;
  is_confidential: boolean;

  is_name_approved: boolean;
  is_brd_approved: boolean;
  is_on_roadmap: boolean;

  prfaq_approval_status: PrfaqApprovalStatus;
  prfaq_link: string | null;

  program_comments: string | null;

  item_metadata: ItemMetadata;
}

/**
 * Form values for creating or editing an RPN program
 * Uses snake_case to match backend conventions
 */
export interface CreateRpnFormValues {
  program_name: string;
  product_line: SelectProps.Option | null;
  program_status: SelectProps.Option | null;
  is_confidential: ApprovalStatus;
  is_name_approved: ApprovalStatus;
  is_brd_approved: ApprovalStatus;
  is_on_roadmap: ApprovalStatus;
  is_prfaq_approved: PrfaqApprovalStatus;
  prfaq_link: string;
  program_comments: string;
}

/**
 * Form errors for CreateRpn form validation
 */
export interface CreateRpnFormErrors {
  program_name?: string;
  product_line?: string;
  program_status?: string;
  is_confidential?: string;
  is_name_approved?: string;
  is_brd_approved?: string;
  is_on_roadmap?: string;
  is_prfaq_approved?: string;
  prfaq_link?: string;
  program_comments?: string;
}

/**
 * Product line option for the select dropdown
 */
export interface ProductLineOption {
  label: string;
  value: string;
}

/**
 * Props for the CreateRpn form component
 */
export interface CreateRpnFormProps {
  on_cancel: () => void;
}

/**
 * Initial form values for CreateRpn form
 */
export const INITIAL_CREATE_RPN_FORM_VALUES: CreateRpnFormValues = {
  program_name: '',
  product_line: null,
  program_status: null,
  is_confidential: null,
  is_name_approved: null,
  is_brd_approved: null,
  is_on_roadmap: null,
  is_prfaq_approved: null,
  prfaq_link: '',
  program_comments: '',
};

// ============================================================================
// API Payload Interfaces (used in service layer)
// ============================================================================

/**
 * Formatted RPN values for API submission (Create)
 * - rpn_id is null for create operations
 * - prfaq_link can be null if PRFAQ is not approved
 * - program_comments can be null if not provided
 * - All other fields are required
 */
export interface FormattedCreateRpnValues {
  rpn_id: null;
  rpn_alias: string;
  product_line_id: number;
  product_line: string;
  program_status: string;
  is_confidential: boolean;
  is_name_approved: boolean;
  is_brd_approved: boolean;
  is_on_roadmap: boolean;
  prfaq_approval_status: PrfaqApprovalStatus;
  prfaq_link: string | null;
  program_comments: string | null;
  item_metadata: ItemMetadata;
}

/**
 * Formatted RPN values for API submission (Update)
 * - rpn_id is required for update operations
 * - prfaq_link can be null if PRFAQ is not approved
 * - program_comments can be null if not provided
 * - All other fields are required
 */
export interface FormattedUpdateRpnValues {
  rpn_id: number;
  rpn_alias: string;
  product_line_id: number;
  product_line: string;
  program_status: string;
  is_confidential: boolean;
  is_name_approved: boolean;
  is_brd_approved: boolean;
  is_on_roadmap: boolean;
  prfaq_approval_status: PrfaqApprovalStatus;
  prfaq_link: string | null;
  program_comments: string | null;
  item_metadata: ItemMetadata;
}
